import { useCallback, useState, type FunctionComponent } from "react";
import NavHomeDashboardAdminPartial from "../../partials/admin/home/home/nav-home-dashboard.admin.partials";
// import StatsHomeDashboardAdminPartial from "../../partials/admin/home/home/stats-home-dashboard.admin.partials";
import CampaignMenuAdminPartial from "../../partials/admin/home/home/campaign-menu.admin.partials";
import { Link, useSearchParams } from "react-router-dom";
import SearchIcon from "../../assets/svgs/common/search.svg";
import { Input } from "../../components/ui/input";
import { Plus, XIcon } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useSearchCampaignsAdminHttp } from "../../hooks/http/admin/campaigns/search-campaigns.admin.http";
import {
	CampaignStatus,
	type ICampaign,
} from "../../types/models/campaign.models.types";
import { debounce, handleHttpError } from "../../utils/utils";
import { useToast } from "../../hooks/use-toast";

export const HomeAdminScreen: FunctionComponent = () => {
	const [search, setSearch] = useState("");
	const [searchData, setSearchData] = useState<ICampaign[]>([]);
	const [typing, setTyping] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [statusParams] = useSearchParams();
	const [status, setStatus] = useState<CampaignStatus>(
		(statusParams.get("status") as CampaignStatus) || CampaignStatus.live,
	);

	const { toast } = useToast();

	const { execute: searchCampaignsHttp, fetching: searching } =
		useSearchCampaignsAdminHttp();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const debouncedSearch = useCallback(
		debounce(async (searchValue: string) => {
			const searchString = searchValue.trim();
			if (!searchString) {
				return;
			}
			const response = await searchCampaignsHttp({
				body: null,
				queryParams: {
					query: searchString,
					status: status,
				},
			});
			if (!response.success) {
				return handleHttpError(response, toast);
			}
			setSearchData(response.data.campaigns);
			setTyping(false);
		}, 500),
		[status],
	);

	function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
		setSearch(e.target.value);
		if (e.target.value.trim().length) {
			setSearchData([]);
			debouncedSearch(e.target.value);
			setTyping(true);
		}
		if (e.target.value.trim().length === 0) {
			setSearchData([]);
			document.getElementById("getCampaigns")?.click();
		}
	}

	return (
		<div className="flex flex-col flex-1 gap-8 px-8 pt-16">
			<NavHomeDashboardAdminPartial hiddenbutton>
				<div className="flex items-center gap-6 w-3/5">
					<div className="flex items-center gap-4 w-full">
						{statusParams.get("status") === "salesDraft" ? (
							<div className="w-full" />
						) : (
							<div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10 w-full">
								<SearchIcon />
								<Input
									value={search}
									onChange={(e) => {
										handleSearch(e);
									}}
									className="bg-transparent border-none font-semibold text-base placeholder:text-secondary"
									placeholder="Search by campaign name"
								/>

								{search.trim().length > 0 && (
									<XIcon
										onClick={() => {
											setSearch("");
											setSearchData([]);
											document.getElementById("getCampaigns")?.click();
										}}
										className="right-5 absolute cursor-pointer"
									/>
								)}
							</div>
						)}

						<DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
							<DropdownMenuTrigger className="bg-tertiarybuttonbg p-4 border border-border rounded-xl cursor-pointer">
								<Plus
									className={`transition-all duration-200 ${dropdownOpen ? "rotate-45" : ""}`}
								/>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className="bg-tertiarybuttonbg mt-2"
								collisionPadding={34}
							>
								<Link to="/admin/brands">
									<DropdownMenuItem
										className="flex justify-end items-center p-3 w-full text-primarybuttonbg"
										textValue="Create campaign"
									>
										<div className="flex items-center gap-1">
											<Plus className="mr-1 w-4 h-4" />
											Create campaign
										</div>
									</DropdownMenuItem>
								</Link>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
				{/* <Link to="sample">
          <Button className="flex items-center gap-1 bg-tertiarybuttonbg px-[38px] py-[28px] border border-white rounded-lg font-semibold text-base text-white tracking-wider">
            Create sample list <ArrowTopRightIcon />
          </Button>
        </Link> */}
			</NavHomeDashboardAdminPartial>
			{/* <StatsHomeDashboardAdminPartial /> */}
			<CampaignMenuAdminPartial
				searchData={searchData}
				search={search}
				setSearch={setSearch}
				searching={searching || typing}
				status={status}
				setStatus={setStatus}
			/>
		</div>
	);
};
